import type { Message } from '../../i18n/types';

/**
 * Texts of a manual run's autotests (i18n/en.ts, i18n/ru.ts); keys start with
 * `runs.autotests.`.
 */
export const runAutotestsEn = {
  'runs.autotests.title': 'Autotests',
  'runs.autotests.hint':
    'Checks whose test case has an autotest run in GitLab. When the autotests finish, the result goes into the check by itself; a result someone recorded stays.',
  'runs.autotests.linked': {
    one: '{count} check has an autotest',
    other: '{count} checks have autotests',
  },
  'runs.autotests.start': 'Start autotests',
  'runs.autotests.startAgain': 'Start again for checks not run',
  'runs.autotests.started': 'Autotests started in GitLab',
  'runs.autotests.startFailed': 'Autotests did not start',
  'runs.autotests.blocked.production':
    'The run is in Production: autotests are not started from it.',
  'runs.autotests.blocked.completed':
    'The run is completed: reopen it to start autotests or take their results.',
  'runs.autotests.pipeline': 'Pipeline #{id}',
  'runs.autotests.status.pending': 'Queued',
  'runs.autotests.status.running': 'Running',
  'runs.autotests.status.success': 'Finished',
  'runs.autotests.status.failed': 'Finished with failures',
  'runs.autotests.status.cancelled': 'Cancelled',
  'runs.autotests.status.error': 'Did not start',
  'runs.autotests.summary': 'Classes: {classes} · checks: {checks} · {environment}',
  'runs.autotests.waiting':
    'The results go into the checks when the autotests finish. Refreshed every 15 seconds.',
  'runs.autotests.applied': 'In the run: passed {passed}, failed {failed}, skipped {skipped}',
  'runs.autotests.missing': 'not in the report: {count} — do them by hand',
  'runs.autotests.kept': 'already recorded: {count} — left as they were',
  'runs.autotests.badge': 'Autotest',
  'runs.autotests.report': 'Report',
  'runs.autotests.create.label': 'Start the autotests with the run',
  'runs.autotests.create.hint': {
    one: '{count} check has an autotest ({projects}). Its result goes into the check by itself when the autotest finishes in GitLab.',
    other:
      '{count} checks have autotests ({projects}). Their results go into the checks by themselves when the autotests finish in GitLab.',
  },
  'runs.autotests.create.production':
    '{count} checks have autotests, but the environment is Production: they will not start.',
} as const;

export const runAutotestsRu: Record<keyof typeof runAutotestsEn, Message> = {
  'runs.autotests.title': 'Автотесты',
  'runs.autotests.hint':
    'Пункты, у кейса которых есть автотест, гоняются в GitLab. Когда автотесты закончатся, результат встанет в пункт сам; то, что отмечено руками, не перезаписывается.',
  'runs.autotests.linked': {
    one: '{count} пункт с автотестом',
    few: '{count} пункта с автотестами',
    many: '{count} пунктов с автотестами',
    other: '{count} пункта с автотестами',
  },
  'runs.autotests.start': 'Запустить автотесты',
  'runs.autotests.startAgain': 'Запустить ещё раз для неотмеченных',
  'runs.autotests.started': 'Автотесты запущены в GitLab',
  'runs.autotests.startFailed': 'Автотесты не запустились',
  'runs.autotests.blocked.production': 'Прогон на Production: автотесты из него не запускаются.',
  'runs.autotests.blocked.completed':
    'Прогон завершён: переоткройте его, чтобы запустить автотесты или принять их результаты.',
  'runs.autotests.pipeline': 'Прогон #{id}',
  'runs.autotests.status.pending': 'В очереди',
  'runs.autotests.status.running': 'Идёт',
  'runs.autotests.status.success': 'Закончен',
  'runs.autotests.status.failed': 'Закончен с падениями',
  'runs.autotests.status.cancelled': 'Отменён',
  'runs.autotests.status.error': 'Не запустился',
  'runs.autotests.summary': 'Классов: {classes} · пунктов: {checks} · {environment}',
  'runs.autotests.waiting':
    'Результаты встанут в пункты, когда автотесты закончатся. Обновляется каждые 15 секунд.',
  'runs.autotests.applied': 'В прогоне: прошло {passed}, упало {failed}, пропущено {skipped}',
  'runs.autotests.missing': 'нет в отчёте: {count} — пройти руками',
  'runs.autotests.kept': 'уже были отмечены: {count} — оставлены как есть',
  'runs.autotests.badge': 'Автотест',
  'runs.autotests.report': 'Отчёт',
  'runs.autotests.create.label': 'Запустить автотесты вместе с прогоном',
  'runs.autotests.create.hint': {
    one: '{count} пункт связан с автотестом ({projects}). Результат встанет в пункт сам, когда автотест в GitLab закончится.',
    few: '{count} пункта связаны с автотестами ({projects}). Результаты встанут в пункты сами, когда автотесты в GitLab закончатся.',
    many: '{count} пунктов связаны с автотестами ({projects}). Результаты встанут в пункты сами, когда автотесты в GitLab закончатся.',
    other:
      '{count} пункта связаны с автотестами ({projects}). Результаты встанут в пункты сами, когда автотесты в GitLab закончатся.',
  },
  'runs.autotests.create.production':
    'Пунктов с автотестами: {count}, но окружение — Production: автотесты не запустятся.',
};
